import { DestroyRef, Directive, ElementRef, inject, input, output } from '@angular/core'
import { HapticService } from '../../services/haptic-service/haptic.service'
import {
  OVERLAY_EASE_IN_OUT,
  OVERLAY_EASE_OUT,
  OVERLAY_PAGE_TAP_EXIT_DURATION,
  OVERLAY_RETURN_DURATION,
  overlayExitDuration
} from '../../animations/overlay-motion'

/** Share of the screen width a slow drag must cover to go back. */
const BACK_DISTANCE_RATIO = 0.15
/** Shortest flick that goes back, in pixels. */
const BACK_FLICK_DISTANCE = 24
/** Flick speed that goes back, in pixels per millisecond. */
const BACK_FLICK_VELOCITY = 0.6

/** Edge gesture for page overlays; row actions and vertical scrolling keep their own touches. */
@Directive({
  selector: '[awPageBackSwipe]',
  host: {
    '(touchstart)': 'onStart($event)',
    '(touchend)': 'onEnd($event)',
    '(touchcancel)': 'onCancel()'
  }
})
export class PageBackSwipeDirective {
  readonly awPageBackSwipe = input(true)
  /** Backdrop that lightens as the page moves away. */
  readonly pageBackSwipeBackdrop = input<HTMLElement>()
  readonly backSwiped = output<void>()
  readonly dismissalStarted = output<void>()
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly haptics = inject(HapticService)
  private start: { x: number; y: number; time: number; id: number } | undefined
  private offset = 0
  /** How far the finger moved; decides whether the swipe goes back. `offset` also holds any entrance travel left. */
  private travel = 0
  /** Where the page was when the swipe took over, for example part way through its entrance. */
  private origin = 0
  private horizontal = false
  private completionTimer: number | undefined
  private dismissing = false

  constructor() {
    const element = this.element.nativeElement
    // A host listener would check the page and every view above it on each move. Moves only write styles, so they skip it.
    const move = (event: TouchEvent): void => this.onMove(event)

    element.addEventListener('touchmove', move, { passive: false })
    inject(DestroyRef).onDestroy(() => {
      element.removeEventListener('touchmove', move)

      if (this.completionTimer !== undefined) {
        window.clearTimeout(this.completionTimer)
      }
    })
  }

  /**
   * Accepts one finger starting at the left edge of the page.
   * @param {TouchEvent} event Touch start on the page surface.
   */
  onStart(event: TouchEvent): void {
    if (this.dismissing) {
      return
    }

    this.onCancel()

    const touch = event.touches[0]

    if (!this.awPageBackSwipe() || event.touches.length !== 1 || !touch || touch.clientX > 32) {
      return
    }

    this.start = { x: touch.clientX, y: touch.clientY, time: event.timeStamp, id: touch.identifier }
    this.setLayers('transform', 'opacity')
  }

  /**
   * Locks to horizontal travel and moves the page with the finger.
   * @param {TouchEvent} event Touch movement on the page surface.
   */
  onMove(event: TouchEvent): void {
    const start = this.start
    const touch = Array.from(event.touches).find(item => item.identifier === start?.id)

    if (!start || !touch) {
      return
    }

    if (!this.awPageBackSwipe() || event.touches.length !== 1) {
      this.onCancel()

      return
    }

    const x = touch.clientX - start.x
    const y = touch.clientY - start.y

    if (!this.horizontal) {
      if (Math.max(Math.abs(x), Math.abs(y)) < 10) {
        return
      }

      if (x <= 0 || Math.abs(y) >= x) {
        this.onCancel()

        return
      }

      this.horizontal = true
      this.prepareSwipe()
    }

    event.preventDefault()
    this.travel = x
    this.offset = Math.max(0, this.origin + x)

    // Only compositor properties change while the page follows the finger.
    const backdrop = this.pageBackSwipeBackdrop()

    this.element.nativeElement.style.transform = `translateX(${this.offset}px)`

    if (backdrop) {
      backdrop.style.opacity = `${1 - Math.min(1, this.offset / window.innerWidth)}`
    }
  }

  /**
   * Commits a long or fast swipe; short swipes return to the starting position.
   * @param {TouchEvent} event Touch release used to measure the gesture duration.
   */
  onEnd(event: TouchEvent): void {
    const start = this.start
    const elapsed = start ? event.timeStamp - start.time : 0
    const completed =
      this.horizontal &&
      (this.travel >= window.innerWidth * BACK_DISTANCE_RATIO ||
        (this.travel >= BACK_FLICK_DISTANCE && elapsed > 0 && this.travel / elapsed >= BACK_FLICK_VELOCITY))

    if (!completed || !this.awPageBackSwipe()) {
      this.onCancel()

      return
    }

    // Back and Close buttons give their own tap feedback before calling `dismiss`.
    this.haptics.swipeThreshold()
    this.dismiss()
  }

  /** Finishes dismissal from the current position, including during an active drag. */
  dismiss(): void {
    if (this.dismissing) {
      return
    }

    this.dismissing = true
    this.start = undefined
    this.horizontal = false
    this.dismissalStarted.emit()

    const panel = this.element.nativeElement
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const transform = window.getComputedStyle(panel).transform
    const swiped = this.offset > 0
    // A swipe keeps its momentum and leaves sooner when part-swiped; Back starts from rest, so it eases in first.
    const duration = swiped
      ? overlayExitDuration(panel.offsetWidth - this.offset, panel.offsetWidth)
      : OVERLAY_PAGE_TAP_EXIT_DURATION
    const easing = swiped ? OVERLAY_EASE_OUT : OVERLAY_EASE_IN_OUT

    panel.style.animation = 'none'
    panel.style.transition = 'none'
    panel.style.transform = transform
    // Commit the current position before starting the exit transition.
    panel.getBoundingClientRect()
    panel.style.transition = reducedMotion ? 'none' : `transform ${duration}ms ${easing}`
    panel.style.transform = 'translateX(100%)'
    this.setBackdrop('0', reducedMotion ? 'none' : `opacity ${duration}ms ${easing}`)

    if (reducedMotion) {
      this.backSwiped.emit()
    } else {
      this.completionTimer = window.setTimeout(() => {
        this.completionTimer = undefined
        this.backSwiped.emit()
      }, duration)
    }
  }

  /** Returns cancelled gestures to rest without navigating. */
  onCancel(): void {
    if (this.dismissing) {
      return
    }

    if (this.horizontal) {
      const panel = this.element.nativeElement
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      panel.style.transition = reducedMotion ? 'none' : `transform ${OVERLAY_RETURN_DURATION}ms ${OVERLAY_EASE_OUT}`
      panel.style.transform = ''
      this.setBackdrop('', reducedMotion ? 'none' : `opacity ${OVERLAY_RETURN_DURATION}ms ${OVERLAY_EASE_OUT}`)
    }

    if (this.start) {
      this.setLayers('', '')
    }

    this.start = undefined
    this.offset = 0
    this.travel = 0
    this.origin = 0
    this.horizontal = false
  }

  /**
   * Stops the page and backdrop animating so they follow the finger. A swipe can start while the page still enters
   * or springs back, so it starts from where the page is instead of snapping to rest.
   */
  private prepareSwipe(): void {
    const panel = this.element.nativeElement
    const backdrop = this.pageBackSwipeBackdrop()
    const matrix = /^matrix\(([^)]+)\)$/.exec(window.getComputedStyle(panel).transform)

    // A computed 2D transform is `matrix(a, b, c, d, x, y)`; at rest it is `none`.
    this.origin = Math.max(0, Number(matrix?.[1].split(',')[4]) || 0)
    panel.style.animation = 'none'
    panel.style.transition = 'none'

    if (backdrop) {
      backdrop.style.animation = 'none'
      backdrop.style.transition = 'none'
    }
  }

  /**
   * Gives the page and backdrop their own layers when an edge touch starts, so the browser builds them before the
   * page moves rather than on its first moving frame.
   * @param {string} panel `will-change` for the page; empty releases its layer.
   * @param {string} backdrop `will-change` for the backdrop; empty releases its layer.
   */
  private setLayers(panel: string, backdrop: string): void {
    this.element.nativeElement.style.willChange = panel

    const element = this.pageBackSwipeBackdrop()

    if (element) {
      element.style.willChange = backdrop
    }
  }

  /**
   * Moves the backdrop with the page after a swipe ends, and drops its layer.
   * @param {string} opacity Inline opacity; empty restores the resting value.
   * @param {string} transition Inline transition for this change.
   */
  private setBackdrop(opacity: string, transition: string): void {
    const backdrop = this.pageBackSwipeBackdrop()

    if (backdrop) {
      backdrop.style.animation = 'none'
      backdrop.style.transition = transition
      backdrop.style.opacity = opacity
      backdrop.style.willChange = ''
    }
  }
}
